/**
 * FeeTree.tsx
 * Hierarchical tree display for broker fee configurations
 *
 * Renders a compact, navigable tree following AssetClass taxonomy:
 * Asset Class Type → Market Segment → Sub-Asset Class → Instrument
 *
 * Features:
 * - Expand/collapse for category nodes
 * - Selection state for driving fee editor
 * - Fee indicator icons for nodes with direct configurations
 * - Kebab menu with level-dependent actions
 * - Search filtering with match highlighting
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, Banknote, MoreVertical } from 'lucide-react';

import {
  EmptyState,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@enfomentis/ui';

import type { BrokerFeeConfiguration } from '@/types/schema/Broker';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';

import { buildFeeTree } from './buildFeeTree';
import type { FeeTreeNode, FeeTreeNodeType, FeeTreeActionType } from './types';

// =============================================================================
// Constants
// =============================================================================

/** Pixels of indentation per tree depth level */
const INDENT_PX = 16;

/** Base left padding for all rows */
const BASE_PADDING_PX = 8;

/** "Add child category" label per node type. null = no add-child action. */
const ADD_CHILD_LABELS: Record<FeeTreeNodeType, string | null> = {
  'asset-class': 'Add Market Segment',
  'market-segment': 'Add Sub-Asset Class',
  'sub-asset-class': null,
  'instrument': null,
};

/** "Add instrument" label per node type. null = no add-instrument action (leaf nodes). */
const ADD_INSTRUMENT_LABELS: Record<FeeTreeNodeType, string | null> = {
  'asset-class': 'Add Instrument',
  'market-segment': 'Add Instrument',
  'sub-asset-class': 'Add Instrument',
  'instrument': null,
};

// =============================================================================
// Tree Filter & Highlight Helpers
// =============================================================================

/**
 * Filter tree nodes by label match, retaining matching nodes and their ancestors.
 * Returns the filtered tree and a set of directly-matched node IDs (for highlighting).
 * Matches against node.label only (not path segments) per requirement.
 */
function filterFeeTree(
  nodes: FeeTreeNode[],
  query: string
): { filtered: FeeTreeNode[]; matchedIds: Set<string> } {
  const queryLower = query.toLowerCase();
  const matchedIds = new Set<string>();

  function filterNode(node: FeeTreeNode): FeeTreeNode | null {
    const labelMatches = node.label.toLowerCase().includes(queryLower);
    if (labelMatches) {
      matchedIds.add(node.id);
    }

    // Recursively filter children
    const filteredChildren = node.children
      .map((child) => filterNode(child))
      .filter((child): child is FeeTreeNode => child !== null);

    // Include this node if it matches OR has matching descendants
    if (labelMatches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  }

  const filtered = nodes
    .map((n) => filterNode(n))
    .filter((n): n is FeeTreeNode => n !== null);

  return { filtered, matchedIds };
}

/**
 * Highlight the first occurrence of `query` within `text`.
 * Returns plain text when no match; otherwise returns a React fragment
 * with the matched substring wrapped in a <mark> element.
 */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-warning-200 text-inherit rounded-sm">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// =============================================================================
// Props
// =============================================================================

interface FeeTreeProps {
  /** Broker ID for stable state reset detection */
  brokerId: string;

  /** Fee configurations from the selected broker */
  feeConfigurations: BrokerFeeConfiguration[];

  /** Currently selected node ID (null if none selected) */
  selectedNodeId: string | null;

  /** Callback when a node is selected */
  onSelectNode: (nodeId: string | null) => void;

  /** Callback when a row action is triggered from the kebab menu.
   *  Optional — when omitted, kebab menus are not rendered (read-only mode). */
  onAction?: (action: FeeTreeActionType, node: FeeTreeNode) => void;

  /** Search query for filtering tree nodes by label.
   *  When non-empty, only matching nodes and their ancestors are shown. */
  searchQuery?: string;
}

// =============================================================================
// Component
// =============================================================================

export const FeeTree: React.FC<FeeTreeProps> = ({
  brokerId,
  feeConfigurations,
  selectedNodeId,
  onSelectNode,
  onAction,
  searchQuery,
}) => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** Set of expanded node IDs */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  /** Currently focused node ID (keyboard navigation) */
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  /** Ref map for scrolling nodes into view */
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  /** Ref to tree container for focus-stealing guard */
  const treeRef = useRef<HTMLDivElement>(null);

  /** Previous selectedNodeId for focus sync guard */
  const prevSelectedRef = useRef<string | null>(null);

  /** Ref for current focusedNodeId — synced during render for stale-closure-free callbacks */
  const focusedNodeIdRef = useRef<string | null>(null);

  /** Ref for current visibleNodeIds — synced during render for stale-closure-free callbacks */
  const visibleNodeIdsRef = useRef<string[]>([]);

  /** Previous focusedNodeId that triggered scroll — guards against transient scroll jitter */
  const prevScrolledFocusRef = useRef<string | null>(null);

  /** Ref for isKeyboardMode — synced during render for effect reads without dep */
  const isKeyboardModeRef = useRef(false);

  /** Whether user is navigating via keyboard (shows focus ring) */
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);

  // ---------------------------------------------------------------------------
  // Reset State on Broker Change
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setExpandedIds(new Set());
    setFocusedNodeId(null);
    setIsKeyboardMode(false);
  }, [brokerId]);

  // ---------------------------------------------------------------------------
  // Memoized Tree
  // ---------------------------------------------------------------------------

  const tree = useMemo(() => {
    return buildFeeTree(feeConfigurations, {
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    });
  }, [feeConfigurations]);

  // ---------------------------------------------------------------------------
  // Search Filtering
  // ---------------------------------------------------------------------------

  const trimmedQuery = searchQuery?.trim() ?? '';
  const isSearching = trimmedQuery.length > 0;

  const { filtered: filteredTree, matchedIds } = useMemo(() => {
    if (!isSearching) {
      return { filtered: tree, matchedIds: new Set<string>() };
    }
    return filterFeeTree(tree, trimmedQuery);
  }, [tree, trimmedQuery, isSearching]);

  // ---------------------------------------------------------------------------
  // Derived Node Sets
  // ---------------------------------------------------------------------------

  // First node ID for roving tabindex fallback (when nothing selected)
  const firstNodeId = filteredTree[0]?.id ?? null;

  // Parent map for focus-fallback ancestor walk on collapse.
  // Uses full tree (not filtered) so focus recovery works when hidden by search.
  const parentMap = useMemo(() => {
    const parents = new Map<string, string | null>();
    const collect = (nodes: FeeTreeNode[], parentId: string | null): void => {
      for (const node of nodes) {
        parents.set(node.id, parentId);
        collect(node.children, node.id);
      }
    };
    collect(tree, null);
    return parents;
  }, [tree]);

  // Visible node IDs in render order (for Up/Down navigation).
  // Uses filteredTree; when searching, all nodes are expanded.
  const visibleNodeIds = useMemo(() => {
    const ids: string[] = [];
    const collectVisible = (nodes: FeeTreeNode[]): void => {
      for (const node of nodes) {
        ids.push(node.id);
        if (node.children.length > 0 && (isSearching || expandedIds.has(node.id))) {
          collectVisible(node.children);
        }
      }
    };
    collectVisible(filteredTree);
    return ids;
  }, [filteredTree, expandedIds, isSearching]);

  // Render-body ref sync (per CODING_STANDARDS Live Sync Patterns)
  focusedNodeIdRef.current = focusedNodeId;
  visibleNodeIdsRef.current = visibleNodeIds;
  isKeyboardModeRef.current = isKeyboardMode;

  // ---------------------------------------------------------------------------
  // Selection & Focus Validation
  // ---------------------------------------------------------------------------

  // Consolidated focus validation & sync effect.
  // Handles: (1) falling back to nearest visible ancestor when focus becomes
  // invisible (collapsed parent), (2) syncing focus with external selection
  // changes, (3) re-aligning focus when search is cleared and the selected
  // node reappears.
  // Reads focusedNodeId and isKeyboardMode from refs to avoid depending on them
  // (which would re-run on keyboard-driven focus changes or pointer interactions).
  useEffect(() => {
    let nextFocusedId = focusedNodeIdRef.current;

    // (1) Fall back to nearest visible ancestor if focused node is hidden
    if (nextFocusedId && !visibleNodeIds.includes(nextFocusedId)) {
      const visited = new Set<string>();
      let ancestor = parentMap.get(nextFocusedId) ?? null;
      while (ancestor && !visibleNodeIds.includes(ancestor)) {
        if (visited.has(ancestor)) break; // defensive cycle guard
        visited.add(ancestor);
        ancestor = parentMap.get(ancestor) ?? null;
      }
      nextFocusedId = ancestor;
    }

    // (2) Sync focus with selection changes
    const selectionChanged = selectedNodeId !== prevSelectedRef.current;
    prevSelectedRef.current = selectedNodeId;

    if (selectedNodeId && visibleNodeIds.includes(selectedNodeId)) {
      if (selectionChanged) {
        nextFocusedId = selectedNodeId;
      } else if (nextFocusedId === null && !isKeyboardModeRef.current) {
        // Re-align focus to selection when not actively keyboard-navigating
        nextFocusedId = selectedNodeId;
      }
    }

    // Single state write — only if focus actually changed
    if (nextFocusedId !== focusedNodeIdRef.current) {
      focusedNodeIdRef.current = nextFocusedId;
      setFocusedNodeId(nextFocusedId);

      // Align DOM focus with visual focus — only when focus is already within
      // the tree or has been lost (collapsed element removed from DOM).
      // Avoids stealing focus from editor/other panes on external selection sync.
      if (nextFocusedId) {
        const active = document.activeElement;
        const focusInTree = !active
          || !active.isConnected
          || active === document.body
          || treeRef.current?.contains(active);
        if (focusInTree) {
          const el = rowRefs.current.get(nextFocusedId);
          if (el?.isConnected) el.focus({ preventScroll: true });
        }
      }
    }
  }, [selectedNodeId, visibleNodeIds, parentMap]);

  // ---------------------------------------------------------------------------
  // Scroll Into View
  // ---------------------------------------------------------------------------

  // Scroll focused node into view — guarded against transient intermediate states
  useEffect(() => {
    if (focusedNodeId && focusedNodeId !== prevScrolledFocusRef.current) {
      prevScrolledFocusRef.current = focusedNodeId;
      const element = rowRefs.current.get(focusedNodeId);
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    } else if (!focusedNodeId) {
      prevScrolledFocusRef.current = null;
    }
  }, [focusedNodeId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleExpand = useCallback((nodeId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleRowClick = useCallback(
    (nodeId: string): void => {
      focusedNodeIdRef.current = nodeId;
      setFocusedNodeId(nodeId);
      onSelectNode(nodeId);
    },
    [onSelectNode]
  );

  const handleRowFocus = useCallback((nodeId: string): void => {
    if (nodeId === focusedNodeIdRef.current) return;
    focusedNodeIdRef.current = nodeId;
    setFocusedNodeId(nodeId);
  }, []);

  const moveFocus = useCallback(
    (direction: 'up' | 'down'): void => {
      const currentFocused = focusedNodeIdRef.current;
      const visible = visibleNodeIdsRef.current;
      if (visible.length === 0) return;

      const currentIndex = currentFocused
        ? visible.indexOf(currentFocused)
        : -1;

      let nextIndex: number;
      if (currentIndex === -1) {
        // No current focus — ArrowUp goes to last, ArrowDown goes to first
        nextIndex = direction === 'up' ? visible.length - 1 : 0;
      } else if (direction === 'up') {
        nextIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
      } else {
        nextIndex =
          currentIndex >= visible.length - 1
            ? visible.length - 1
            : currentIndex + 1;
      }

      const nextId = visible[nextIndex];
      if (nextId) {
        // Update ref immediately so subsequent calls within the same frame
        // read the correct value (avoids stale closure during rapid key repeat)
        focusedNodeIdRef.current = nextId;
        setFocusedNodeId(nextId);
        // Move DOM focus to the new row
        const element = rowRefs.current.get(nextId);
        if (element) {
          element.focus({ preventScroll: true });
        }
      }
    },
    []
  );

  const handleChevronClick = useCallback(
    (e: React.MouseEvent, nodeId: string): void => {
      e.stopPropagation();
      if (isSearching) {
        e.preventDefault();
        return;
      }
      toggleExpand(nodeId);
    },
    [toggleExpand, isSearching]
  );

  // ---------------------------------------------------------------------------
  // Render Helpers
  // ---------------------------------------------------------------------------

  const renderNode = (node: FeeTreeNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const isExpanded = isSearching ? hasChildren : expandedIds.has(node.id);
    const isSelected = node.id === selectedNodeId;
    const isFocused = node.id === focusedNodeId;

    // Roving tabindex: focused node is tabbable, or first node when nothing focused
    const isTabbable = isFocused || (!focusedNodeId && node.id === firstNodeId);

    // Kebab menu: determine available actions for this node
    const addChildLabel = ADD_CHILD_LABELS[node.type];
    const addInstrumentLabel = ADD_INSTRUMENT_LABELS[node.type];
    const hasAnyAction = onAction && (addChildLabel || addInstrumentLabel || node.hasDirectFee);

    return (
      <div key={node.id}>
        {/* Row */}
        <div
          ref={(el) => {
            if (el) {
              rowRefs.current.set(node.id, el);
            } else {
              rowRefs.current.delete(node.id);
            }
          }}
          role="treeitem"
          data-node-id={node.id}
          aria-selected={isSelected}
          aria-expanded={hasChildren ? isExpanded : undefined}
          tabIndex={isTabbable ? 0 : -1}
          className={`
            flex items-center h-9 cursor-pointer rounded-sm
            ${isSelected ? 'bg-default-100' : 'hover:bg-default-100/50'}
            outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-inset
          `}
          style={{ paddingLeft: node.depth * INDENT_PX + BASE_PADDING_PX }}
          onClick={() => handleRowClick(node.id)}
          onFocus={() => handleRowFocus(node.id)}
          onKeyDown={(e) => {
            // Up/Down navigation
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setIsKeyboardMode(true);
              moveFocus('up');
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setIsKeyboardMode(true);
              moveFocus('down');
              return;
            }
            // Enter/Space selects
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsKeyboardMode(true);
              onSelectNode(node.id);
              return;
            }
            // ArrowRight/ArrowLeft: prevent default scroll during search
            if (hasChildren && e.key === 'ArrowRight' && isSearching) {
              e.preventDefault();
              setIsKeyboardMode(true);
              return;
            }
            if (hasChildren && e.key === 'ArrowLeft' && isSearching) {
              e.preventDefault();
              setIsKeyboardMode(true);
              return;
            }
            // ArrowRight expands
            if (hasChildren && e.key === 'ArrowRight' && !isExpanded) {
              e.preventDefault();
              setIsKeyboardMode(true);
              toggleExpand(node.id);
              return;
            }
            // ArrowLeft collapses
            if (hasChildren && e.key === 'ArrowLeft' && isExpanded) {
              e.preventDefault();
              setIsKeyboardMode(true);
              toggleExpand(node.id);
            }
          }}
        >
          {/* Chevron (expand/collapse) */}
          {hasChildren ? (
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              className="p-1 -ml-1 hover:bg-default-200 rounded-sm"
              onClick={(e) => handleChevronClick(e, node.id)}
              onMouseDown={(e) => e.preventDefault()}
            >
              {isExpanded ? (
                <ChevronDown size={16} className="text-text-secondary" />
              ) : (
                <ChevronRight size={16} className="text-text-secondary" />
              )}
            </button>
          ) : (
            // Spacer to align labels when no chevron
            <span className="w-6" />
          )}

          {/* Label */}
          <span className="flex-1 text-sm text-text-primary truncate ml-1">
            {matchedIds.has(node.id) ? highlightMatch(node.label, trimmedQuery) : node.label}
          </span>

          {/* Fee indicator */}
          {node.hasDirectFee && (
            <Banknote
              size={14}
              className="text-success-600 mr-2 shrink-0"
              aria-label="Has fee configuration"
            />
          )}

          {/* Kebab menu — only rendered when onAction is provided and node has actions */}
          {hasAnyAction && (
            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    // tabIndex={-1}: intentional mouse-only access to avoid
                    // interfering with tree roving-tabindex keyboard navigation
                    tabIndex={-1}
                    aria-label="Fee actions"
                  >
                    <MoreVertical size={16} />
                  </Button>
                </DropdownTrigger>
                <DropdownMenu aria-label="Fee actions">
                  {[
                    // "Add [Child Category Level]" — only for nodes with category children
                    ...(addChildLabel
                      ? [
                          <DropdownItem
                            key="add-child"
                            onPress={() => onAction('add-child', node)}
                          >
                            {addChildLabel}
                          </DropdownItem>,
                        ]
                      : []),
                    // "Add Instrument" — available at all non-leaf category levels
                    ...(addInstrumentLabel
                      ? [
                          <DropdownItem
                            key="add-instrument"
                            onPress={() => onAction('add-instrument', node)}
                          >
                            {addInstrumentLabel}
                          </DropdownItem>,
                        ]
                      : []),
                    // "Copy fees to..." — only when node has a direct fee config
                    ...(node.hasDirectFee
                      ? [
                          <DropdownItem
                            key="copy"
                            onPress={() => onAction('copy', node)}
                          >
                            Copy fees to...
                          </DropdownItem>,
                        ]
                      : []),
                    // "Remove" — only when node has a direct fee config
                    ...(node.hasDirectFee
                      ? [
                          <DropdownItem
                            key="remove"
                            className="text-warning-600"
                            onPress={() => onAction('remove', node)}
                          >
                            Remove
                          </DropdownItem>,
                        ]
                      : []),
                  ]}
                </DropdownMenu>
              </Dropdown>
            </div>
          )}
        </div>

        {/* Children (only render when expanded) */}
        {hasChildren && isExpanded && (
          <div role="group">
            {node.children.map((child) => renderNode(child))}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Empty State
  // ---------------------------------------------------------------------------

  if (tree.length === 0) {
    return (
      <EmptyState
        title="No fee configurations"
        description="Add fee configurations to see them organized by asset class"
      />
    );
  }

  // No search results (tree has data, but nothing matches the query)
  if (isSearching && filteredTree.length === 0) {
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-text-secondary">No matching items</p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={treeRef}
      role="tree"
      aria-label="Fee configuration hierarchy"
      className="py-2"
      onPointerDown={() => setIsKeyboardMode(false)}
    >
      {filteredTree.map((node) => renderNode(node))}
    </div>
  );
};
